import React, { useMemo, useState } from 'react';
// Components
import TaskAccordionItem from './TaskAccordionItem';
import TaskPauseModal from './TaskPauseModal';
// Helpers
import { cartLineKey, groupCartByParent } from 'helpers/CartGrouping';

/**
 * The order's tasks, one expandable row each - all collapsed until one is opened.
 */
const TasksAccordion = ({
  cartDetails,
  orderDetails,
  hasSubmittedBefore,
  hasDeviation,
  setHasSubmittedBefore,
  setHasDeviation,
  deviationEnabled = true,
  refreshCartDetails,
  preparationTasks,
  busyTaskId,
  onTaskAction,
}) => {
  const [openTaskKey, setOpenTaskKey] = useState(null);
  const [pausingTask, setPausingTask] = useState(null);

  /**
   * A bundle's products each get their own task and share the bundle's cartLineId, so the
   * product is what tells them apart. Tasks created before the bundle change carry no
   * productId - those still match on the line alone.
   */
  const taskForCartItem = (item) => {
    const tasks = preparationTasks?.tasks ?? [];
    const onThisLine = tasks.filter((t) => t?.cartLineId === item?.id);
    if (onThisLine.length < 2) {
      return onThisLine[0];
    }
    return onThisLine.find((t) => t?.productId === item?.productId);
  };

  const groups = useMemo(() => groupCartByParent(cartDetails), [cartDetails]);
  // Step numbers stay 1..N across the whole order, bundles included.
  const stepOf = (item) => cartDetails.indexOf(item) + 1;

  // Pause is the only action that needs more input than a tap.
  const handleTaskAction = (action, task) => {
    if (action === 'pause') {
      setPausingTask(task);
      return;
    }
    onTaskAction(action, task);
  };

  return (
    <>
      {pausingTask && (
        <TaskPauseModal
          isOpen={Boolean(pausingTask)}
          setIsOpen={() => setPausingTask(null)}
          taskLabel={pausingTask?.cartLineDisplay}
          isDisabled={busyTaskId === pausingTask?.taskId}
          onConfirm={(pauseReasonId) => {
            onTaskAction('pause', pausingTask, { pauseReasonId });
            setPausingTask(null);
          }}
        />
      )}

      <div className="tasks-note">
        <b>One task at a time.</b> Pause or finish the task you started before starting another
        one. No Prep items are included automatically.
      </div>

      <div className="card tasks-accordion">
        {groups.map((group) => {
          const rows = group.items.map((item) => {
            const key = cartLineKey(item);
            const task = taskForCartItem(item);
            return (
              <TaskAccordionItem
                key={key}
                cartItem={item}
                orderMapId={orderDetails?.orderMapId}
                orderId={orderDetails?.id}
                preparationStatus={orderDetails?.preparationStatus}
                hasSubmittedBefore={hasSubmittedBefore}
                hasDeviation={hasDeviation}
                setHasSubmittedBefore={setHasSubmittedBefore}
                setHasDeviation={setHasDeviation}
                taskIndex={stepOf(item)}
                refreshCartDetails={refreshCartDetails}
                task={task}
                isTaskBusy={Boolean(task?.taskId) && busyTaskId === task.taskId}
                onTaskAction={handleTaskAction}
                isOpen={openTaskKey === key}
                onToggle={() => setOpenTaskKey((prev) => (prev === key ? null : key))}
                isGrouped={Boolean(group.parent)}
                deviationEnabled={deviationEnabled}
              />
            );
          });

          if (!group.parent) {
            return <React.Fragment key={group.key}>{rows}</React.Fragment>;
          }

          return (
            <div className="task-group" key={group.key}>
              <div className="task-group-head">
                {group.parent.image && (
                  <img className="task-group-thumb" src={group.parent.image} alt={group.parent.name} />
                )}
                <div className="task-group-text">
                  <span className="task-group-name">{group.parent.name}</span>
                  <span className="task-group-meta">
                    <span className="task-group-sku">{group.parent.sku}</span>
                    <span className="task-group-count">
                      {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </span>
                </div>
              </div>
              <div className="task-group-items">{rows}</div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TasksAccordion;
